import { type ComponentType, type ReactElement, useCallback, useRef } from "react";

import { type ApolloQueryResult } from "@apollo/client/core/types";
import {
    FixedSizeList,
    type FixedSizeListProps,
    type ListChildComponentProps,
    type ListOnItemsRenderedProps,
} from "react-window";

import { type GetLensesListQuery } from "../../lenses/api/queries";
import { type VirtualizedListData } from "../models";

interface VirtualizedListProps<TListItem, TData> extends Omit<FixedSizeListProps, "itemCount" | "itemData"> {
    data: VirtualizedListData<TListItem, TData>;
    children: ComponentType<ListChildComponentProps>;
    hasMore: boolean;
    renderMore?: (visibleStopIndex: number) => Promise<ApolloQueryResult<GetLensesListQuery>>;
}

export const VirtualizedList = <TListItem, TData>({
    data,
    hasMore,
    height,
    width,
    itemSize,
    renderMore,
    children,
    innerRef,
    outerRef,
}: VirtualizedListProps<TListItem, TData>): ReactElement => {
    const { list } = data;
    const stopIndexRef = useRef(0);
    const itemsCount = hasMore ? list.length + 1 : list.length;

    const onItemsRendered = useCallback(
        async (props: ListOnItemsRenderedProps): Promise<void> => {
            const { visibleStopIndex } = props;
            if (visibleStopIndex === list.length && stopIndexRef.current !== visibleStopIndex) {
                stopIndexRef.current = visibleStopIndex;
                await renderMore?.(visibleStopIndex);
            }
        },
        [renderMore, list, stopIndexRef],
    );

    return (
        <FixedSizeList
            height={height}
            itemCount={itemsCount}
            itemData={data}
            itemSize={itemSize}
            width={width}
            onItemsRendered={onItemsRendered}
            innerRef={innerRef}
            outerRef={outerRef}
        >
            {children}
        </FixedSizeList>
    );
};
